import { useT } from "../../../shared/i18n/i18n";
import { AtSign, Bell, CircleCheckBig, UserRoundPlus } from "lucide-react";
import type { ReactNode } from "react";
import { useNotifications, type NotificationItem } from "../../../entities/notification/model/use-notifications";
import { cn } from "../../../shared/lib/cn";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

function actorName(notification: NotificationItem) {
  return notification.actor?.displayName ?? notification.actor?.email ?? "Someone";
}

function formatWhen(at: Date | string) {
  const date = new Date(at);
  const sameDay = new Date().toDateString() === date.toDateString();
  const time = `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  if (sameDay) return time;
  return `${new Intl.DateTimeFormat("en", { day: "numeric", month: "short" }).format(date)} · ${time}`;
}

const kindIcons: Record<NotificationItem["kind"], ReactNode> = {
  assigned: <UserRoundPlus className="size-3.5" />,
  mention: <AtSign className="size-3.5" />,
  status_changed: <CircleCheckBig className="size-3.5" />,
};

function notificationText(notification: NotificationItem): string {
  const title = notification.payload.workObjectTitle ?? "a work item";
  if (notification.kind === "mention") return `${actorName(notification)} mentioned you in “${title}”`;
  if (notification.kind === "assigned") return `${actorName(notification)} assigned you to “${title}”`;
  return `“${title}” → ${notification.payload.statusName ?? "new status"} by ${actorName(notification)}`;
}

// The in-app inbox: mentions, assignments and status changes of the viewer's
// items. Clicking a row marks it read and jumps to the item on its board.
export function InboxSheet({
  onOpenChange,
  onOpenNotification,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onOpenNotification: (target: { boardId: string; workObjectId: string }) => void;
  open: boolean;
}) {
  const t = useT();
  const notifications = useNotifications({ listEnabled: open });

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent className="flex w-96 flex-col gap-0 p-0" side="right">
        {/* pr-12 keeps the header actions clear of the sheet's close button. */}
        <SheetHeader className="flex-row items-center gap-2 space-y-0 py-3 pl-4 pr-12">
          <Bell className="size-4 text-muted-foreground" />
          <SheetTitle className="flex-1 text-sm">{t("inbox.title")}</SheetTitle>
          {notifications.unreadCount > 0 ? (
            <Button className="text-muted-foreground" onClick={() => void notifications.markAllRead()} size="sm" variant="ghost">
              {t("inbox.markAllRead")}
            </Button>
          ) : null}
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
          {notifications.items.length === 0 && !notifications.isLoading ? (
            <div className="flex flex-col items-center gap-2 px-4 py-16 text-center">
              <Bell className="size-6 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">{t("inbox.allCaughtUp")}</p>
              <p className="text-xs text-muted-foreground/70">{t("inbox.whatItIs")}</p>
            </div>
          ) : null}
          {notifications.items.map((notification) => (
            <button
              className={cn(
                "flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent/50",
                notification.readAt === null && "bg-muted/40",
              )}
              key={notification.id}
              onClick={() => {
                void notifications.markRead([notification.id]);
                if (notification.boardId && notification.workObjectId) {
                  onOpenNotification({ boardId: notification.boardId, workObjectId: notification.workObjectId });
                  onOpenChange(false);
                }
              }}
              type="button"
            >
              <span
                className={cn(
                  "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full",
                  notification.readAt === null ? "bg-primary/15 text-primary" : "bg-muted/60 text-muted-foreground",
                )}
              >
                {kindIcons[notification.kind]}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xs leading-snug text-foreground/90">{notificationText(notification)}</span>
                {notification.payload.snippet ? (
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">{notification.payload.snippet}</span>
                ) : null}
              </span>
              <span className="shrink-0 pt-0.5 text-[11px] tabular-nums text-muted-foreground/70">
                {formatWhen(notification.createdAt)}
              </span>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
